import { Controller } from "@hotwired/stimulus"

// Drives the customer "manage reservation" change form: as the customer edits
// dates/guest count, debounces a fetch to the change_pricing_preview endpoint
// and updates the price-delta banner + availability message, gating submit
// on availability. Named distinctly from the admin `reservation_change_controller`
// (a different controller with a different target/JSON shape for rental transfers)
// to avoid colliding with that registration.
export default class extends Controller {
  static targets = ["startDate", "endDate", "guestCount", "banner", "message", "submit"]
  static values = {
    previewUrl: String
  }

  static DEBOUNCE_MS = 300

  connect() {
    this._debounceTimer = null
    this._abortController = null
  }

  disconnect() {
    clearTimeout(this._debounceTimer)
    this._abortController?.abort()
  }

  schedulePreview() {
    clearTimeout(this._debounceTimer)
    this._debounceTimer = setTimeout(() => this.preview(), this.constructor.DEBOUNCE_MS)
  }

  disableSubmit() {
    if (this.hasSubmitTarget) this.submitTarget.disabled = true
  }

  async preview() {
    const startDate = this.hasStartDateTarget ? this.startDateTarget.value : null
    const endDate = this.hasEndDateTarget ? this.endDateTarget.value : null
    const guestCount = this.hasGuestCountTarget ? this.guestCountTarget.value : null

    if (!startDate || !endDate) {
      this.clear()
      return
    }

    this._abortController?.abort()
    this._abortController = new AbortController()

    const params = new URLSearchParams({ start_date: startDate, end_date: endDate })
    if (guestCount) params.set("guest_count", guestCount)

    try {
      const response = await fetch(`${this.previewUrlValue}?${params}`, {
        signal: this._abortController.signal,
        headers: { Accept: "application/json" }
      })

      const data = await response.json().catch(() => ({}))
      this.render(data)
    } catch (error) {
      if (error.name === "AbortError") return
      this.showMessage("We couldn't check pricing for those dates. Please try again.")
      this.setSubmitDisabled(true)
    }
  }

  render(data) {
    if (data.available === false) {
      this.hideBanner()
      this.showMessage(data.unavailability_reason || "These dates aren't available.")
      this.setSubmitDisabled(true)
      return
    }

    this.hideMessage()
    this.showBanner(data)
    this.setSubmitDisabled(false)
  }

  showBanner(data) {
    if (!this.hasBannerTarget) return

    const adjustment = Number(data.price_adjustment || 0)
    let text
    let classes

    if (adjustment > 0) {
      text = `${this.formatCurrency(adjustment)} will be charged`
      classes = "bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
    } else if (adjustment < 0) {
      const refund = data.refund_amount != null ? Number(data.refund_amount) : Math.abs(adjustment)
      text = `${this.formatCurrency(refund)} will be refunded`
      classes = "bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-300"
    } else {
      text = "No price change"
      classes = "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
    }

    this.bannerTarget.className = `mt-4 rounded-md p-4 text-sm font-medium ${classes}`
    this.bannerTarget.textContent = text
  }

  hideBanner() {
    if (this.hasBannerTarget) this.bannerTarget.classList.add("hidden")
  }

  showMessage(text) {
    if (!this.hasMessageTarget) return
    this.messageTarget.textContent = text
    this.messageTarget.classList.remove("hidden")
  }

  hideMessage() {
    if (this.hasMessageTarget) this.messageTarget.classList.add("hidden")
  }

  clear() {
    this.hideBanner()
    this.hideMessage()
    this.setSubmitDisabled(false)
  }

  setSubmitDisabled(disabled) {
    if (this.hasSubmitTarget) this.submitTarget.disabled = disabled
  }

  formatCurrency(amount) {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount)
  }
}
